import { apiClass } from '@/base/class/api.class'
import { ENDPOINTS } from '@/base/const/endpoints.const'
import { ToolsClass } from '@/base/class/tools.class'
import { ADS_BANNER_SIZE, ADS_SOURCE_ID } from '@/base/const/ads.const'
import { REPORT_LEVEL, reportMessage } from '@/base/utils/errorReporting'

const state = {
  isAdDataLoaded: false,
  data: null,
  size: ADS_BANNER_SIZE.desktop,
  showCommercialBreake: false,
  commercialBreakClosed: false,
  serviceId: null,
  email: null,
}
const mutations = {
  SET_ADS_DATA(state, data) {
    state.data = data
  },
  SET_ADS_SIZE(state, value) {
    state.size = value
  },
  SET_ADS_SHOW_COMMERCIAL_BREAKE(state, value) {
    state.showCommercialBreake = value
  },
  SET_ADS_COMMERCIAL_BREAK_CLOSED(state, value) {
    state.commercialBreakClosed = value
  },
  SET_ADS_IS_AD_DATA_LOADED(state, value) {
    state.isAdDataLoaded = value
  },
  SET_ADS_SERVICE_ID(state, value) {
    state.serviceId = value
  },
  SET_ADS_EMAIL(state, value) {
    state.email = value
  },
}
const actions = {
  getAdsBanners({ commit }, { serviceId, transRemoteID, paywayId, email }) {
    commit('SET_ADS_SERVICE_ID', serviceId)
    commit('SET_ADS_EMAIL', email)
    const endpoint = `${ENDPOINTS.ads.getBannersList}?serviceId=${serviceId}&sourceId=${ADS_SOURCE_ID}&paywayId=${paywayId}&email=${email}&transaction_id=${transRemoteID}`
    return apiClass
      .getAds(endpoint)
      .then((response) => {
        commit('SET_ADS_DATA', response.body)
        if (ToolsClass.isMobileScreenSize()) {
          commit('SET_ADS_SIZE', ADS_BANNER_SIZE.mobile)
        }
        return Promise.resolve()
      })
      .catch((error) => {
        return Promise.reject(error)
      })
      .finally(() => {
        commit('SET_ADS_IS_AD_DATA_LOADED', true)
      })
  },
  sendAdsBannerViewConfirmation({ commit, state }, { creationId, transRemoteID }) {
    const endpoint = `${ENDPOINTS.ads.sendBannerViewConfirmation}?serviceId=${state.serviceId}&creationId=${creationId}&email=${state.email}&transaction_id=${transRemoteID}`
    return apiClass.getAds(endpoint)
  },
}
/**
 * Serwisy, dla których nieoczekiwany kształt odpowiedzi `/ads` został już zgłoszony.
 *
 * Gettery reklamowe są czytane z kilku komponentów przy każdym renderze, więc bez tego jedna
 * odpowiedź generowałaby serię identycznych zdarzeń. Klucz to `serviceId`, bo pytanie brzmi
 * „ilu serwisom adserver odpowiada czymś, co nie jest listą", a nie „ile razy przeliczył się getter".
 */
const reportedUnexpectedAdsData = new Set()

/**
 * Metoda zgłaszająca odpowiedź adserwera, która nie jest listą banerów.
 *
 * Po dołożeniu `getBannerList` ten stan przestał rzucać, czyli **przestał być widoczny w Sentry sam
 * z siebie** - a odpowiada za niewyświetlone reklamy, których nikt inny nie policzy.
 */
function reportUnexpectedAdsData(state) {
  const key = String(state.serviceId ?? null)
  if (reportedUnexpectedAdsData.has(key)) {
    return
  }
  reportedUnexpectedAdsData.add(key)
  reportMessage('Ads: odpowiedź adserwera nie jest listą banerów', {
    level: REPORT_LEVEL.warning,
    tags: { flow: 'ads' },
    extra: {
      serviceId: state.serviceId ?? null,
      // Typ i `length` rozstrzygają, czy backend przysyła string (np. HTML błędu), czy obiekt
      // opakowujący listę - bez tego nie da się napisać zgłoszenia do adserwera.
      typDanych: typeof state.data,
      czyTablica: Array.isArray(state.data),
      dlugosc: state.data?.length ?? null,
    },
  })
}

/**
 * Lista banerów z odpowiedzi adserwera.
 *
 * `SET_ADS_DATA` zapisuje `response.body` bez sprawdzenia typu, a strażnik `!state.data.length`
 * przepuszczał wszystko, co ma `length` - w tym string. Wtedy `state.data.find` nie istnieje
 * i getter rzucał `data.find is not a function` (6 grup w Sentry na 2 doby: komunikat per silnik
 * razy hashowana nazwa chunka `useApp.*`, więc każda z nich wyglądała na pojedynczy incydent).
 * `isAdsBannerDataSet` miał `Array.isArray` od początku - pozostałe trzy gettery nie.
 */
const getBannerList = (state) => {
  if (Array.isArray(state.data)) {
    return state.data
  }
  if (state.data) {
    reportUnexpectedAdsData(state)
  }
  return []
}

const findBanner = (state, commercialBreak) =>
  getBannerList(state).find((banner) => banner.targetDevice === state.size && banner.commercialBreak === commercialBreak)

const getters = {
  isAdsBannerDataSet(state) {
    return getBannerList(state).length
  },
  isCommercialBreakDataSet(state) {
    return !!findBanner(state, true)
  },
  getBannerToDisplay(state) {
    return findBanner(state, false) ?? false
  },
  getCommercialBreak(state) {
    return findBanner(state, true) ?? false
  },
}

export default {
  state,
  mutations,
  actions,
  getters,
}
